<script>
import { format } from 'date-fns';

export default {
  name: 'DeviceCard',
  props: {
    subscription: {
      type: Object,
      required: true,
    },
    testing: {
      type: Boolean,
      default: false,
    },
    testResult: {
      type: Object,
      default: null,
    },
    showTest: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['test', 'delete'],
  computed: {
    buildLabel() {
      if (!this.subscription.build_number) {
        return '';
      }

      return `${this.$t('NOTIFICATION_DEVICES.DEVICE_CARD.BUILD')} ${this.subscription.build_number}`;
    },
    deviceTitle() {
      return (
        this.subscription.device_label ||
        this.subscription.device_name ||
        this.subscription.identifier
      );
    },
    detailLine() {
      if (this.subscription.subscription_type === 'browser_push') {
        return this.subscription.browser_endpoint;
      }

      return [this.subscription.brand_name, this.subscription.device_platform]
        .filter(Boolean)
        .join(' • ');
    },
    iconName() {
      if (this.subscription.subscription_type === 'browser_push') {
        return 'globe';
      }

      const label = `${this.deviceTitle || ''}`.toLowerCase();
      return label.includes('ipad') || label.includes('tablet')
        ? 'tablet'
        : 'phone';
    },
    statusClass() {
      if (this.subscription.stale) {
        return 'bg-n-ruby-3 text-n-ruby-11';
      }

      if (this.subscription.days_since_refresh >= 14) {
        return 'bg-n-amber-2 text-n-amber-12';
      }

      return 'bg-n-teal-3 text-n-teal-11';
    },
    statusLabel() {
      if (this.subscription.stale) {
        return this.$t('NOTIFICATION_DEVICES.DEVICE_CARD.STATUS_STALE');
      }

      if (this.subscription.days_since_refresh >= 14) {
        return this.$t('NOTIFICATION_DEVICES.DEVICE_CARD.STATUS_AGING');
      }

      return this.$t('NOTIFICATION_DEVICES.DEVICE_CARD.STATUS_ACTIVE');
    },
    testResultClass() {
      if (this.testResult?.success) {
        return 'bg-n-teal-3 text-n-teal-11';
      }

      return 'bg-n-ruby-3 text-n-ruby-11';
    },
    testResultMessage() {
      if (this.testResult?.success) {
        return this.$t('NOTIFICATION_DEVICES.DEVICE_CARD.TEST_SUCCESS');
      }

      return this.$t('NOTIFICATION_DEVICES.DEVICE_CARD.TEST_FAILED', {
        error: this.testResult?.error,
      });
    },
  },
  methods: {
    formatDate(value) {
      return format(new Date(value), 'dd MMM yyyy');
    },
  },
};
</script>

<template>
  <article
    class="flex flex-col gap-4 p-4 border border-solid border-n-weak rounded-xl bg-n-alpha-2"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="flex items-start gap-3 min-w-0">
        <div
          class="flex items-center justify-center w-10 h-10 rounded-xl bg-n-background border border-solid border-n-weak flex-shrink-0"
        >
          <fluent-icon :icon="iconName" size="18" class="text-n-slate-12" />
        </div>
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm font-medium text-n-slate-12 break-words">
              {{ deviceTitle }}
            </span>
            <span v-if="buildLabel" class="text-xs text-n-slate-11">
              {{ buildLabel }}
            </span>
          </div>
          <p v-if="detailLine" class="mt-1 text-xs text-n-slate-11 break-all">
            {{ detailLine }}
          </p>
        </div>
      </div>

      <span
        class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap"
        :class="statusClass"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div class="grid gap-2 text-xs text-n-slate-11 sm:grid-cols-2">
      <div>
        <span class="font-medium text-n-slate-12">
          {{ `${$t('NOTIFICATION_DEVICES.DEVICE_CARD.REGISTERED')}:` }}
        </span>
        {{ formatDate(subscription.created_at) }}
      </div>
      <div>
        <span class="font-medium text-n-slate-12">
          {{ `${$t('NOTIFICATION_DEVICES.DEVICE_CARD.LAST_REFRESH')}:` }}
        </span>
        {{ formatDate(subscription.updated_at) }}
        {{
          `(${$t('NOTIFICATION_DEVICES.DEVICE_CARD.DAYS_AGO', {
            days: subscription.days_since_refresh,
          })})`
        }}
      </div>
    </div>

    <div
      v-if="testResult"
      class="px-3 py-2 text-xs rounded-lg"
      :class="testResultClass"
    >
      {{ testResultMessage }}
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <woot-button
        v-if="showTest"
        size="small"
        color-scheme="success"
        :is-loading="testing"
        @click="$emit('test')"
      >
        {{ $t('NOTIFICATION_DEVICES.DEVICE_CARD.TEST_PUSH') }}
      </woot-button>
      <woot-button size="small" variant="clear" @click="$emit('delete')">
        {{ $t('NOTIFICATION_DEVICES.DEVICE_CARD.DELETE') }}
      </woot-button>
    </div>
  </article>
</template>
